import { TooltipTrigger } from "@visiblethread/react-components";
import clsx from "clsx";
import { REQUIREMENT_VERIFICATION } from "constants/proposals";
import {
  BadgeCheck,
  CircleDashed,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { oneOf } from "prop-types";

import styles from "./VerificationBadge.module.scss";

// Maps a requirement's mechanical-verification outcome to a color/icon/tooltip.
// sectionRef is deliberately NOT part of this — it's informational-only provenance
// (not mechanically verified), so it must never be adorned with any of these badges.
const VERIFICATION_CONFIG = {
  [REQUIREMENT_VERIFICATION.VERIFIED]: {
    label: "Verified",
    tooltip: "An exact quote match was found in the source document.",
    icon: BadgeCheck,
    className: styles.verified,
  },
  [REQUIREMENT_VERIFICATION.FUZZY_MATCH]: {
    label: "Partial Match",
    tooltip:
      "A close (paraphrased) match was found in the source document — worth a quick check.",
    icon: CircleDashed,
    className: styles.fuzzyMatch,
  },
  [REQUIREMENT_VERIFICATION.UNVERIFIED]: {
    label: "Unverified",
    tooltip: "No matching quote could be found in the source document.",
    icon: TriangleAlert,
    className: styles.unverified,
  },
  [REQUIREMENT_VERIFICATION.USER_PROVIDED]: {
    label: "User Added",
    tooltip: "This requirement was added or edited by a user.",
    icon: UserRound,
    className: styles.userProvided,
  },
};

const VerificationBadge = ({ verification }) => {
  const config = VERIFICATION_CONFIG[verification];
  if (!config) {
    return null;
  }

  const Icon = config.icon;

  return (
    <TooltipTrigger content={config.tooltip} delay={200}>
      <span className={clsx(styles.badge, config.className)}>
        <Icon aria-hidden="true" size={14} />
        {config.label}
      </span>
    </TooltipTrigger>
  );
};

VerificationBadge.propTypes = {
  verification: oneOf(Object.values(REQUIREMENT_VERIFICATION)),
};

export default VerificationBadge;
